/**
 * ReportSectionEditor — the "Draft Report" column (col-3).
 *
 * Renders the docx-editor.dev <DocxEditor> bound to the provider's
 * editorRef + documentBuffer. The heavy editor bundle is lazy-loaded so
 * it stays out of the app-wide entry chunk (the provider is mounted in
 * <Layout/>); it only loads once a case is active and this column mounts.
 *
 * Reads everything from <ReportBuilderEditorProvider/>: editorRef,
 * documentBuffer, autosave (lastSavedAt), download.
 */
import {
  Suspense,
  lazy,
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";

import { Download } from "lucide-react";
import type { EditorView } from "prosemirror-view";

import { Skeleton } from "@components/ui/skeleton";

import { useCapabilities } from "@hooks/queries/use-me";

import { docxBytesShowEmptyFirstPageInPrint } from "@utils/docx-differential-headers";

import { DraftConflictModal } from "../components/DraftConflictModal";
import { EditorLeaseBanner } from "../components/EditorLeaseBanner";
import {
  CODE_OF_CONDUCT_HEADING_TITLE,
  CONFLICT_OF_INTEREST_HEADING_TITLE,
  EXPECTATION_SETTINGS_HEADING_TITLE,
  LIST_OF_DOCUMENTS_HEADING_TITLE,
  OTHER_ATTENDEES_HEADING_TITLE,
  QUESTION_TO_ANSWER_HEADING_TITLE,
  UNUSUAL_CIRCUMSTANCES_HEADING_TITLE,
} from "../constants";
import { EditorOverlays } from "./EditorOverlays";
import { formatRelative } from "./autosave";
import { useReportBuilderEditor } from "./context";
import { insertDocumentsTable } from "./documents-table-insert";
import { alignBlankParagraphIndents } from "./indent-normalize";
import { applyModuleContent } from "./module-insert";
import type { AnchorPlacement } from "./module-insert";
import { PREAMBLE_DEFINITIONS } from "./preamble";
import { insertQtaBlock } from "./qta-insert";
import { dropPlugin, pastePlugin, slotPlugin } from "./slots";
import { useSpellcheckOverlay } from "./spellcheck/useSpellcheckOverlay";
import { computeFitZoom } from "./zoom-fit";

// Where CoC should land when the uploaded .docx has no CODE OF CONDUCT
// heading yet. Tried in order; first match wins.
//
// The "before" entries are ordered by how *early* in a typical Australian
// medico-legal template each heading appears — we prefer the earliest
// match so CoC lands near the top of the body (right after the case
// preamble) rather than mid-document. Heading texts were sourced from
// the 68-template doctor sample: anchors that appear 5+ times across
// templates are included.
//
// Final "after" entries are last-resort textual anchors for templates
// that don't have any of the above section headings — they target the
// opening paragraph everyone writes ("Thank you for your referral…"
// or "I write regarding…").
const COC_FALLBACK_ANCHORS: AnchorPlacement[] = [
  ...EXPECTATION_SETTINGS_HEADING_TITLE.map((m) => ({
    position: "before" as const,
    match: m,
  })),
  { position: "before", match: "Service Standards" },
  { position: "before", match: "Expertise" }, // Walterfang + a few others (10x)
  { position: "before", match: "Methodology" }, // 9x
  { position: "before", match: "Introduction" }, // 12x
  { position: "before", match: "Sources of Information" }, // 10x
  { position: "before", match: "Reason for Referral" }, // 11x
  { position: "before", match: "Summary of Reason for Referral" }, // 6x
  { position: "before", match: "Documentation" }, // 15x
  { position: "before", match: "Documents" }, // 6x
  { position: "before", match: "Background" }, // 14x
  { position: "after", match: "Thank you for your referral" },
  { position: "after", match: "I write regarding" },
];

// Lazy: defers @eigenpal/docx-editor-react (~5M) out of the entry chunk.
const DocxEditor = lazy(() =>
  import("@eigenpal/docx-editor-react").then((m) => ({ default: m.DocxEditor }))
);

const EXTERNAL_PLUGINS = [slotPlugin, dropPlugin, pastePlugin];

type Variant = "inline" | "drawer";

export const ReportSectionEditor = ({
  variant = "inline",
  finalised = false,
}: {
  variant?: Variant;
  /** Once finalised, the case is locked and the editor goes read-only. The
   *  .docx download stays available (MDRS-336) — the lock is a write lock. */
  finalised?: boolean;
}) => {
  // MDRS-336: the in-editor Word download is capability-gated, not admin-only.
  // MDRS-271 had hidden it from doctors entirely; Nat asked for it back, so it
  // now mirrors the server gate (report:download_docx, on by default for
  // doctors) and stays available on finalised cases too. PDF export remains
  // admin-only — see HeaderActions.
  const { can } = useCapabilities();
  const canDownloadDocx = can("report:download_docx");
  const {
    editorRef,
    documentBuffer,
    isBufferLoading,
    bufferError,
    onEditorChange,
    markEdited,
    isSaving,
    lastSavedAt,
    handleDownload,
    isDownloading,
    draftId,
    moduleContent,
    orderedModules,
    qtaSource,
    documentsSource,
    outlineHints,
    outlineAnchors,
    outlineReady,
    // MDRS-554 / audit RB-8
    draftConflict,
    reloadAfterConflict,
    downloadConflictCopy,
    canEdit,
    leaseMessage,
    takeOverEditing,
    // MDRS-555 / audit RB-9
    flushPendingSave,
  } = useReportBuilderEditor();

  // Flush the debounced autosave when this column goes away (MDRS-555 / audit
  // RB-9) — the user navigating to Assessment Prep / Scribe, or out of the case
  // entirely. It has to happen HERE, not in the provider: the provider is
  // mounted in <Layout/> and survives navigation, so its own unmount only
  // covers logout. What disappears on navigation is <DocxEditor>, and once it
  // has gone `editorRef.current` is null and the .docx can no longer be
  // serialised at all — the pending tick would persist the text and silently
  // drop the document.
  //
  // Reads through a ref so the effect's deps stay empty: `flushPendingSave`
  // changes identity whenever `runSave` does, and a dep on it would make this
  // cleanup fire on ordinary re-renders, saving mid-typing on every keystroke.
  const flushPendingSaveRef = useRef(flushPendingSave);
  flushPendingSaveRef.current = flushPendingSave;
  // useLayoutEffect, NOT useEffect, and that is the whole fix rather than a
  // preference. Passive cleanups for a deleted tree are flushed after the
  // mutation phase, by which point React has destroyed every layout effect in
  // the subtree — `<DocxEditor>`'s `useImperativeHandle` included — and nulled
  // `editorRef`. The flush would then find no editor, `isEditorEmpty()` would
  // report true, and it would return `{ ok: true }` having saved nothing. A
  // LAYOUT cleanup runs during the mutation phase, parent before child, so the
  // handle is still attached and the .docx can still be serialised. Pinned by
  // ReportSectionEditor.ref-lifetime.test.tsx.
  useLayoutEffect(
    () => () => {
      // Fire-and-forget, but not unhandled — nothing is listening once this
      // column is gone.
      // `quiet`: this column is being torn down, so a .docx-failure toast
      // would surface on whatever page the user navigated to.
      void flushPendingSaveRef.current({ quiet: true }).catch(() => undefined);
    },
    []
  );

  const [takingOver, setTakingOver] = useState(false);
  const handleTakeOver = useCallback(async () => {
    setTakingOver(true);
    try {
      await takeOverEditing();
    } finally {
      setTakingOver(false);
    }
  }, [takeOverEditing]);

  // Re-render the relative "Autosaved N min ago" label on an interval.
  const [now, setNow] = useState(() => lastSavedAt ?? 0);
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 30_000);
    return () => clearInterval(id);
  }, []);
  useEffect(() => {
    if (lastSavedAt) setNow(Date.now());
  }, [lastSavedAt]);

  const savedLabel = isSaving
    ? "Saving…"
    : lastSavedAt
      ? `Autosaved: ${formatRelative(lastSavedAt, now)}`
      : "";

  // Fit the (A4) page width to col-3 by measuring the container and
  // calling the editor's imperative `setZoom`. `initialZoom` alone
  // didn't take effect in 1.3.0 — the page stayed at intrinsic width.
  // ResizeObserver keeps the fit correct when the user toggles the
  // Notes / QA columns (col-3 width changes).
  const pageWrapperRef = useRef<HTMLDivElement | null>(null);
  // The zoom we last auto-applied. MDRS-402: the ResizeObserver also fires on
  // the reflow the user's own zoom (toolbar zoom control) triggers, which kept
  // snapping their zoom back to the fit value. `computeFitZoom` compares the
  // editor's current zoom to this; when they differ the user has taken manual
  // control and we leave their zoom alone.
  const lastAutoZoomRef = useRef<number | null>(null);
  const fitZoomToContainer = useCallback(() => {
    const container = pageWrapperRef.current;
    const editor = editorRef.current;
    if (!container || !editor) return;
    const next = computeFitZoom(
      container.clientWidth,
      editor.getZoom(),
      lastAutoZoomRef.current
    );
    if (next === null) return; // user manually zoomed — respect it
    editor.setZoom(next);
    // Read back the fork's stored value so the next comparison is exact.
    lastAutoZoomRef.current = editor.getZoom();
  }, [editorRef]);

  useEffect(() => {
    if (!pageWrapperRef.current) return;
    const ro = new ResizeObserver(() => fitZoomToContainer());
    ro.observe(pageWrapperRef.current);
    return () => ro.disconnect();
  }, [fitZoomToContainer]);

  // First-page header/footer hide — see `docx-differential-headers.ts`.
  // Templates whose first sectPr indicates "page 1 prints with neither
  // header nor footer" (Dr Sam Jones Empty template) get a
  // `data-hide-first-page-headers` attribute on the section wrapper.
  // A CSS rule in `index.css` then hides eigenpal's h/f slots on the
  // first rendered editor page only — pages 2+ render normally. This
  // does NOT fire for titlePg+first-type-refs templates (e.g. the
  // generated Robin Hood report) where page 1 has its own letterhead
  // that should remain visible.
  const [hideFirstPageHeaders, setHideFirstPageHeaders] = useState(false);
  useEffect(() => {
    if (!documentBuffer) {
      setHideFirstPageHeaders(false);
      return;
    }
    let cancelled = false;
    docxBytesShowEmptyFirstPageInPrint(documentBuffer).then((result) => {
      if (!cancelled) setHideFirstPageHeaders(result);
    });
    return () => {
      cancelled = true;
    };
  }, [documentBuffer]);

  // ── Module insertion: chosen modules → matching template headings ─────
  // The user's uploaded template ships with all sections already drafted.
  // We don't strip or rebuild anything — we just find paragraphs whose
  // text matches a module name (e.g. "Code of Conduct" matches a "CODE
  // OF CONDUCT" heading in the doc) and replace the inline content of
  // the paragraph immediately below with the chosen text. See
  // `./module-insert.ts` for the matching + replacement logic.
  const editorViewRef = useRef<EditorView | null>(null);
  // Spell-check: wrap misspelled words in the visible page DOM with a red
  // squiggle + click-to-fix (PM decorations / native spellcheck can't — the fork
  // paints pages off the off-screen PM). See useSpellcheckOverlay.
  // Pass markEdited so a click-to-fix correction FORCE-arms autosave — its
  // programmatic dispatch doesn't reliably reach the editor's onChange, and the
  // signature compare can miss it, so we don't re-classify (MDRS-405).
  useSpellcheckOverlay(pageWrapperRef, editorViewRef, markEdited);
  // Flipped to true once docx-editor finishes mounting and hands us a
  // PM view. The auto-insert effect below depends on this — the
  // editor's lazy chunk + parseDocx can take well over the 300ms
  // setTimeout debounce, so without this signal the very first run
  // saw `editorViewRef.current === null` and bailed without ever
  // re-firing (MDRS-97). Adding the ready flag as an effect dep
  // means we re-run the moment the view is available.
  const [editorViewReady, setEditorViewReady] = useState(false);

  // MDRS-227: module names whose heading this session has already synthesised
  // via applyModuleContent's anchor-insert (Path B). The debounced insertion
  // effect below re-fires on every async input change; without this, a fire
  // whose in-doc exists-check fails to recognise a previously-inserted heading
  // would add a duplicate every time. Reset whenever a new document loads
  // (tracked by `insertBufferRef`) so a freshly-loaded doc gets fresh inserts.
  const anchorInsertedRef = useRef<Set<string>>(new Set());
  const insertBufferRef = useRef<ArrayBuffer | undefined>(undefined);

  const handleEditorViewReady = useCallback(
    (view: EditorView) => {
      editorViewRef.current = view;
      setEditorViewReady(true);
      fitZoomToContainer();
    },
    [fitZoomToContainer]
  );

  // Focus guardian — keep the editor typable after a toolbar action.
  //
  // Changing the font (or size) while the caret sits in an EMPTY paragraph runs
  // a doc-mutating transaction (the editor mirrors the new font into the blank
  // line so the caret renders at the right height). That relayout races the
  // toolbar's own refocus, and Radix's Select "return focus to the trigger on
  // close" wins the race — then the trigger element is re-rendered and its focus
  // evaporates, leaving focus on <body>. Keystrokes then go nowhere ("I changed
  // the font and can't type"). It only bites on empty paragraphs: with text
  // present the same action is a non-mutating stored-mark change (no relayout),
  // so focus is never lost.
  //
  // When focus falls OUT of the editor region to nothing (relatedTarget null),
  // put it back on the next frame — but only if it truly landed on <body>, so we
  // never fight a real focus move (toolbar input, dialog, sidebar).
  useEffect(() => {
    if (finalised) return;
    const onFocusOut = (e: FocusEvent) => {
      // Only act when focus is heading to NOTHING (relatedTarget null). A move
      // to any real element — toolbar control, dialog, sidebar input — is left
      // alone.
      if (e.relatedTarget !== null) return;
      // Scope to the editor: only recover focus that left one of the editor's
      // own surfaces (a ProseMirror page or its toolbar), so a stray blur-to-
      // nothing elsewhere on the page can't yank the caret back in. The vendored
      // editor roots everything — the off-screen PMs and the portaled toolbar
      // popovers alike — under `.ep-root`.
      const from = e.target as HTMLElement | null;
      if (!from?.closest?.(".ep-root")) return;
      requestAnimationFrame(() => {
        // Restore only if focus truly evaporated (landed on <body>). This page
        // is an editor surface, so "nothing is focused" always means the editor
        // should be.
        const active = document.activeElement;
        if (active === document.body || active === null) {
          editorRef.current?.focus?.();
        }
      });
    };
    document.addEventListener("focusout", onFocusOut, true);
    return () => document.removeEventListener("focusout", onFocusOut, true);
  }, [finalised, editorRef]);

  // Push moduleContent into matching template headings, debounced so
  // typing inside the editor doesn't thrash the PM tree. Q&A sections are
  // skipped — the user authors those responses directly in the docx editor,
  // so we mustn't overwrite the template's questions with seeded text.
  useEffect(() => {
    if (!documentBuffer) return;
    // MDRS-509: wait for the template outline before inserting. Until it
    // settles `outlineAnchors.cocAnchor` is null, so the preamble sections
    // would be placed by the static fallback list — landing ahead of a
    // template-owned Code of Conduct, which autosave then persists. Same shape
    // as the `editorViewReady` gate below (MDRS-97): hold off, and re-run the
    // moment the signal arrives.
    if (!outlineReady) return;
    const handle = window.setTimeout(() => {
      const view = editorViewRef.current;
      if (!view) return;
      // MDRS-227: reset the anchor-insert guard when a new document loads, so a
      // freshly-loaded doc gets fresh inserts while re-fires within one loaded
      // doc are deduped.
      if (insertBufferRef.current !== documentBuffer) {
        insertBufferRef.current = documentBuffer;
        anchorInsertedRef.current = new Set();
      }
      // Build the shared anchor list once per render: outline-discovered
      // CoC anchor (when present) prepended to the static fallback list.
      // All four "preamble" modules use the same anchors — they cluster
      // before the SERVICE STANDARDS / first-section heading.
      const preambleAnchors: AnchorPlacement[] = outlineAnchors?.cocAnchor
        ? [
            { position: "before", match: outlineAnchors.cocAnchor },
            ...COC_FALLBACK_ANCHORS,
          ]
        : COC_FALLBACK_ANCHORS;

      // MDRS-140: the Code of Conduct declaration is no longer auto-inserted —
      // experts select/enter the wording manually. The app only ensures the
      // heading exists (like the other preamble stubs). We therefore no longer
      // resolve any jurisdiction fallback text for CoC.

      // Canonical preamble order (MDRS-60): the four auto-inserted sections
      // share a single insertion anchor, so the order they appear in the
      // final doc is the order they're listed here. Backend templates have
      // historically defined them in a different order
      // (e.g. NSW IMC: CoC → Attendees → Unusual → Conflict), so we re-sort
      // before handing off to applyModuleContent.
      const preambleRank = (name: string): number => {
        if (CODE_OF_CONDUCT_HEADING_TITLE.includes(name)) return 0;
        if (CONFLICT_OF_INTEREST_HEADING_TITLE.includes(name)) return 1;
        if (OTHER_ATTENDEES_HEADING_TITLE.includes(name)) return 2;
        if (UNUSUAL_CIRCUMSTANCES_HEADING_TITLE.includes(name)) return 3;
        return -1;
      };

      const modules = orderedModules
        .filter(
          ({ module }) =>
            !QUESTION_TO_ANSWER_HEADING_TITLE.includes(module.name) &&
            // MDRS-98: the List-of-Documents section is rendered as a
            // 3-column table by `insertDocumentsTable` below, not as
            // plain replaced text. Filtering it out here avoids double-
            // inserting the experts response (once via `m.text`, again
            // via the table flow).
            !LIST_OF_DOCUMENTS_HEADING_TITLE.includes(module.name)
        )
        .sort((a, b) => {
          const ra = preambleRank(a.module.name);
          const rb = preambleRank(b.module.name);
          // Non-preamble modules keep their backend order; preamble modules
          // are pulled into [CoC, Conflict, Attendees, Unusual]. Mixed
          // ordering doesn't matter — non-preamble modules attach to their
          // own existing headings via the existing-heading path, so their
          // position in the input array doesn't affect where they land.
          if (ra === -1 && rb === -1) return 0;
          if (ra === -1) return -1;
          if (rb === -1) return 1;
          return ra - rb;
        })
        .map(({ module }) => {
          const m = moduleContent[module.name];
          // The app-owned "preamble" sections that should always appear in the
          // doc when missing. MDRS-140: all are heading-only stubs — none
          // carries auto-inserted content. A module's own resolved text (a
          // manual choice) is still applied; otherwise the empty-`m.value`
          // path in applyModuleContent inserts just the heading and preserves
          // any existing body text.
          //
          // MDRS-140: Code of Conduct is force-inserted as a HEADING (like the
          // others) but its body stays empty — the expert copies the wording
          // from the CoC panel. If the template docx already carries a CoC
          // heading, applyModuleContent's exists-check attaches to it instead of
          // synthesising a duplicate.
          const isCoc = CODE_OF_CONDUCT_HEADING_TITLE.includes(module.name);
          const isConflict = CONFLICT_OF_INTEREST_HEADING_TITLE.includes(
            module.name
          );
          const isAttendees = OTHER_ATTENDEES_HEADING_TITLE.includes(module.name);
          const isUnusual = UNUSUAL_CIRCUMSTANCES_HEADING_TITLE.includes(
            module.name
          );

          let preambleConfig: Partial<{
            headingLabel: string;
            fallbackAnchors: AnchorPlacement[];
            alwaysInsert: boolean;
          }> = {};
          if (isCoc) {
            preambleConfig = {
              headingLabel: "CODE OF CONDUCT",
              fallbackAnchors: preambleAnchors,
              alwaysInsert: true,
            };
          } else if (isConflict) {
            preambleConfig = {
              headingLabel: "CONFLICT OF INTEREST",
              fallbackAnchors: preambleAnchors,
              alwaysInsert: true,
            };
          } else if (isAttendees) {
            preambleConfig = {
              headingLabel: "INTERPRETER AND/OR OTHER ATTENDEES",
              fallbackAnchors: preambleAnchors,
              alwaysInsert: true,
            };
          } else if (isUnusual) {
            preambleConfig = {
              headingLabel: "UNUSUAL CIRCUMSTANCES",
              fallbackAnchors: preambleAnchors,
              alwaysInsert: true,
            };
          }

          // MDRS-140: no jurisdiction fallback for CoC. A module's own resolved
          // text (a manual choice, if the template still declares CoC choices)
          // is still inserted; otherwise the section is heading-only, exactly
          // like the other three preamble stubs.
          const value = m?.text ?? "";
          return {
            name: module.name,
            value,
            ...preambleConfig,
          };
        });

      // MDRS-97: case templates don't always declare the preamble sections as
      // modules (the VIC IME templates omit them entirely, for example).
      // Without a backing module, the auto-insert above never tags those slots
      // with `alwaysInsert`, so the headings never land in the doc. Synthesise
      // missing ones here so every template gets the preamble headings — the
      // `alwaysInsert` exists-check inside applyModuleContent then skips any
      // heading already present in the template body (avoiding duplicates).
      // MDRS-140: Code of Conduct is one of these heading-only stubs — the
      // heading is force-inserted, but the body stays empty (experts browse-
      // and-copy the wording from the CoC panel). See ./preamble.
      for (const { titles, headingLabel, value } of PREAMBLE_DEFINITIONS) {
        const alreadyPresent = modules.some((m) => titles.includes(m.name));
        if (alreadyPresent) continue;
        modules.push({
          name: headingLabel,
          value,
          headingLabel,
          fallbackAnchors: preambleAnchors,
          alwaysInsert: true,
        });
      }

      applyModuleContent(
        view,
        modules,
        outlineHints ?? undefined,
        anchorInsertedRef.current
      );

      // NOTE (MDRS-227): the QtA and Documents inserts below are NOT covered by
      // the `anchorInsertedRef` guard used for the preamble modules above — they
      // must re-run so late-arriving qtaSource/documentsSource data populates,
      // so a one-insert-per-session guard doesn't fit. They keep their own
      // heading-recognition idempotency (bail when the heading already exists),
      // which shares the same classifier-blind weakness the preamble guard
      // works around. If duplicate Questions/Documents headings are observed on
      // such templates, hardening these two helpers is a follow-up (they'd need
      // to guard only their heading-synthesis path, not content population).
      //
      // After standard module insertion, drop the QtA (Question To Answer)
      // block in if the case has mapped LOI questions but the doc doesn't
      // already contain a QtA heading. Idempotent — once inserted, the
      // helper bails to avoid clobbering the expert's typed answers.
      // Always insert the Questions heading — even when the case has no LOI
      // questions or the template declares no QtA module — as a stub for the
      // expert to fill in. insertQtaBlock is idempotent against an existing
      // heading and preserves any answers the expert has typed.
      {
        insertQtaBlock(view, {
          headingLabel: "RESPONSES TO SPECIFIC QUESTIONS",
          rawQuestions: qtaSource?.rawQuestions ?? "",
          expertsResponse: qtaSource?.expertsResponse ?? "",
          existingHeadingNames: QUESTION_TO_ANSWER_HEADING_TITLE,
          // Outline hints (when loaded) let the QtA anchor/idempotency
          // logic lean on the backend's per-template classification
          // instead of re-running heading heuristics here. Falls back to
          // local detection when null (outline still loading, or the
          // case has no uploaded template).
          ...(outlineHints && { outlineHints }),
        });
      }

      // MDRS-98: render the List-of-Documents schedule as a 3-column table.
      // Always insert the section — even when the case has no documents or the
      // template declares no docs module — so every report carries it.
      // Idempotency lives in `insertDocumentsTable` — when the matched heading
      // already has body content (which our first fire's table satisfies), or
      // an empty heading exists with nothing to add, subsequent fires skip.
      {
        insertDocumentsTable(view, {
          headingLabel: "SUMMARY OF DOCUMENTS PROVIDED",
          summaryText: documentsSource?.summaryText ?? "",
          expertsResponseText: documentsSource?.expertsResponse ?? "",
          ...(outlineHints && { outlineHints }),
          // MDRS-98: when the template's docx body doesn't carry a
          // SUMMARY OF DOCUMENTS PROVIDED heading (e.g. VIC WorkSafe
          // Psych IME — case 951429), fall back to the same anchor
          // list MDRS-97 uses for the preamble auto-inserts. Both runs
          // anchor "before SERVICE STANDARDS" (or the next-best major
          // heading), so the docs section lands right after the 4
          // preambles instead of way at the bottom near the signature.
          fallbackAnchors: preambleAnchors,
        });
      }

      // Align blank lines to the indent of the content they sit under, so
      // the caret / dictation mic lines up with an indented heading instead
      // of sitting outside its indent. Runs last so it also normalises the
      // spacer paragraphs the inserts above just laid down. Idempotent — a
      // settled doc produces zero edits.
      alignBlankParagraphIndents(view);
    }, 300);
    return () => window.clearTimeout(handle);
  }, [
    moduleContent,
    orderedModules,
    documentBuffer,
    qtaSource,
    documentsSource,
    outlineHints,
    outlineAnchors,
    outlineReady,
    editorViewReady,
  ]);

  return (
    <section
      className="flex h-full min-h-0 flex-col bg-[#F5F5F7]"
      data-hide-first-page-headers={hideFirstPageHeaders ? "true" : undefined}
    >
      <div className="flex items-center justify-between gap-3 border-b bg-background-white px-5 py-3">
        <div className="text-sm font-semibold">
          {variant === "drawer" ? "Report Builder" : "Draft Report"}
        </div>
        <div className="flex items-center gap-4">
          {savedLabel ? (
            <span className="text-xs text-foreground-muted">{savedLabel}</span>
          ) : null}
          {canDownloadDocx ? (
            <button
              type="button"
              onClick={handleDownload}
              // Each click is a round trip that also writes an audit row, so a
              // double-click would download twice and log twice. `!draftId`
              // matches the caret's precondition in HeaderActions — without it
              // a draftless case dead-ends on a "save the draft first" toast
              // instead of the control just being unavailable (MDRS-336).
              disabled={isDownloading || !draftId}
              aria-busy={isDownloading}
              className="inline-flex items-center gap-1.5 text-xs font-medium text-primary hover:underline disabled:no-underline disabled:opacity-50"
              title={
                !draftId
                  ? "Save a draft first to download"
                  : isDownloading
                    ? "Preparing your download…"
                    : "Download .docx"
              }
            >
              <Download className="size-3.5" />
              {isDownloading ? "Downloading…" : "Download"}
            </button>
          ) : null}
        </div>
      </div>

      {/* MDRS-554: the advisory lease. Sits above the canvas rather than over
          it so the user can still READ the report they can't currently edit. */}
      <EditorLeaseBanner
        message={leaseMessage}
        onTakeOver={handleTakeOver}
        isTakingOver={takingOver}
      />

      {/* MDRS-554: a save was refused because someone else got there first.
          Blocking by design — autosave is stopped until the user chooses. */}
      <DraftConflictModal
        conflict={draftConflict}
        onDownloadCopy={downloadConflictCopy}
        onReload={reloadAfterConflict}
      />

      <div ref={pageWrapperRef} className="relative min-h-0 flex-1 overflow-auto">
        {/* Only surface a load error when there is NO buffer to show. Once the
            buffer is pinned for the session (see the provider), a later refetch
            failure — e.g. re-minting the draft SAS after an autosave — leaves
            `bufferError` set while the editor keeps working on the pinned buffer;
            without this guard that would paint a false "Failed to load" banner
            over a perfectly functional editor. */}
        {bufferError && !documentBuffer ? (
          <div className="flex h-full items-center justify-center p-6 text-center">
            <div className="max-w-md text-sm text-red-700">
              Failed to load template:{" "}
              {bufferError instanceof Error
                ? bufferError.message
                : String(bufferError)}
            </div>
          </div>
        ) : isBufferLoading || !documentBuffer ? (
          <div className="space-y-3 p-6">
            <Skeleton className="h-8 w-1/3" />
            <Skeleton className="h-4 w-full" />
            <Skeleton className="h-4 w-full" />
            <Skeleton className="h-4 w-2/3" />
            <Skeleton className="h-64 w-full" />
          </div>
        ) : (
          <Suspense
            fallback={
              <div className="space-y-3 p-6">
                <Skeleton className="h-8 w-1/3" />
                <Skeleton className="h-64 w-full" />
              </div>
            }
          >
            <DocxEditor
              ref={editorRef}
              documentBuffer={documentBuffer}
              externalPlugins={EXTERNAL_PLUGINS}
              onChange={onEditorChange}
              onEditorViewReady={handleEditorViewReady}
              onError={(err: Error) => {
                console.error("docx-editor error:", err);
              }}
              // MDRS-271: once finalised the case is locked — make the editor
              // read-only so it matches the server-side write-lock (409) instead
              // of letting the user type edits that can never be saved.
              //
              // MDRS-554 adds the same reasoning for the advisory lease: while
              // someone else holds it, typing here would only earn a conflict
              // dialog at the next autosave tick. `canEdit` already folds in
              // the finalise lock; `finalised` is kept explicit so the
              // MDRS-271 guarantee doesn't depend on the lease's fail-open
              // behaviour.
              readOnly={finalised || !canEdit}
              showToolbar
              // MDRS-402: no Help menu in the Report Builder (keyboard-shortcuts
              // / about aren't relevant to report authoring).
              showHelpMenu={false}
              showRuler={false}
              showZoomControl
              showOutline={false}
              showOutlineButton={false}
              initialZoom={0.7}
            />
            <EditorOverlays editorRef={editorRef} scopeRef={pageWrapperRef} />
          </Suspense>
        )}
      </div>
    </section>
  );
};
